/**
 * Live transcription preview: mic → AudioWorklet → WebSocket → interim text.
 *
 * This is deliberately a PREVIEW and never a system of record. The stored
 * transcript is produced afterwards by the async pipeline, which runs chirp_3 over
 * the uploaded recording — better accuracy, real punctuation, and speaker
 * attribution, none of which the streaming API can give (it refuses diarization
 * outright and punctuates weakly).
 *
 * Everything here therefore fails soft: if the socket never opens, drops, or the
 * browser has no AudioWorklet, the contributor loses the live text and nothing
 * else. The recording still uploads and still gets transcribed.
 */

import { closeGracefully } from './closeGracefully'
import workletUrl from './pcmWorklet.ts?worker&url'

/** Accumulated live text. Finals are cumulative; interim replaces itself.
 *
 * Finals are kept pre-joined rather than as an array. Interims arrive up to ~50x a
 * second and each one re-renders, so deriving the visible text has to be O(interim)
 * — re-joining a growing array of every final (≈4,500 words after 30 minutes) on
 * each one is ~25 KB of string churn per message for text that did not change.
 */
export interface TranscriptState {
    finals: string
    interim: string
}

export interface TranscriptMessage {
    type: string
    text?: string
    isFinal?: boolean
    detail?: string
}

export const emptyTranscript = (): TranscriptState => ({
    finals: '',
    interim: '',
})

/**
 * Fold one server message into the transcript.
 *
 * The interim/final distinction is the whole protocol: an interim is a revision of
 * the utterance in progress and REPLACES the previous one, while a final is
 * committed and appends. Treating them alike duplicates every phrase on screen.
 */
export function applyTranscriptMessage(
    state: TranscriptState,
    message: TranscriptMessage
): TranscriptState {
    if (message.type !== 'transcript') return state
    const text = (message.text ?? '').trim()
    if (!text) return state
    if (message.isFinal) {
        // A final supersedes the interim it was built from.
        return {
            finals: state.finals ? `${state.finals} ${text}` : text,
            interim: '',
        }
    }
    // The recogniser re-sends an unchanged interim often at a ~0.02s cadence.
    // Returning the same object lets the caller skip a render entirely.
    if (text === state.interim) return state
    return { ...state, interim: text }
}

/** The text to show: everything committed, plus the utterance in progress. */
export function renderTranscript(state: TranscriptState): string {
    if (!state.interim) return state.finals
    return state.finals ? `${state.finals} ${state.interim}` : state.interim
}

/** Only the committed text — what a caller would keep if the socket died. */
export function finalTranscript(state: TranscriptState): string {
    return state.finals
}

export function streamUrl(
    locale: string,
    tenantSlug?: string | null,
    recordingId?: string | null
): string {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const params = new URLSearchParams({ locale })
    // Optional: the backend falls back to the anonymous session cookie, so a
    // contributor with no active tenant in the client still resolves.
    if (tenantSlug) params.set('tenant', tenantSlug)
    // Attributes the preview to a recording already reserved (ADR-0016 item 3). The
    // server stores the finalised words on it, so a closed tab or a failed upload no
    // longer loses them. Omitted when nothing was reserved — the socket is unchanged.
    if (recordingId) params.set('recordingId', recordingId)
    return `${protocol}://${window.location.host}/api/v1/ws/speech/stream?${params}`
}

/**
 * How a streaming session ended. The distinction is the whole point: three of
 * these mean the preview text is INCOMPLETE, and until now all four were
 * indistinguishable from success.
 */
export type StreamOutcome =
    /** The socket never opened. No preview at all. */
    | 'never_connected'
    /** The socket, or the server, reported an error. */
    | 'errored'
    /** It opened and then closed while we were still capturing — the silent one. */
    | 'closed_early'
    /** We closed it ourselves when the contributor stopped. */
    | 'completed'

/**
 * What the stream actually covered, in seconds of audio.
 *
 * Derived from bytes rather than wall-clock, because wall-clock cannot tell a
 * socket that was open-but-not-accepting from one that was carrying speech.
 * `streamedSeconds` counts only frames the socket actually took; `droppedSeconds`
 * counts audio the worklet produced while it could not.
 *
 * The ratio of the two against the recording's length is what decides whether the
 * uploaded audio is needed as a fallback.
 */
export interface StreamCoverage {
    outcome: StreamOutcome
    streamedSeconds: number
    droppedSeconds: number
}

/** LINEAR16 mono @ 16 kHz — see `pcmWorklet.ts`. Two bytes per sample. */
const STREAM_BYTES_PER_SECOND = 16000 * 2

export interface LiveTranscriptionOptions {
    stream: MediaStream
    locale: string
    tenantSlug?: string | null
    /** A recording reserved before capture, to attribute the preview to. */
    recordingId?: string | null
    onUpdate: (text: string) => void
    onError?: (reason: string) => void
}

/**
 * Owns the socket and the audio graph for one recording.
 *
 * `start()` resolves once streaming is under way, or throws if the preview cannot
 * be set up — callers should treat that as "no live text", not as a failed
 * recording.
 */
export class LiveTranscriptionSession {
    private ws: WebSocket | null = null
    private context: AudioContext | null = null
    private source: MediaStreamAudioSourceNode | null = null
    private worklet: AudioWorkletNode | null = null
    private state: TranscriptState = emptyTranscript()
    private closed = false
    private sentBytes = 0
    private droppedBytes = 0
    /** Pessimistic until proven otherwise: if nothing changes this, we never connected. */
    private outcome: StreamOutcome = 'never_connected'

    constructor(private readonly options: LiveTranscriptionOptions) {}

    get transcript(): string {
        return finalTranscript(this.state)
    }

    /**
     * What this session covered. Safe to call at any point, including after a
     * failure — a caller that only asks after a clean stop learns nothing about
     * the sessions that matter.
     */
    coverage(): StreamCoverage {
        return {
            outcome: this.effectiveOutcome(),
            streamedSeconds: this.sentBytes / STREAM_BYTES_PER_SECOND,
            droppedSeconds: this.droppedBytes / STREAM_BYTES_PER_SECOND,
        }
    }

    /**
     * How this session ended, **derived** rather than stamped on teardown.
     *
     * `outcome` starts at `never_connected` and is only moved by something going
     * wrong. Success therefore has no event to record it, and stamping it in `stop()`
     * made the answer depend on when you asked: the mic reads coverage *before*
     * stopping the session — it has to, because `stop()` releases it — so every
     * healthy recording reported `never_connected` and warned the contributor their
     * captions had missed something, while the server measured 99% coverage on the
     * same session.
     *
     * Deriving it removes the ordering from the question entirely. A session that
     * carried audio and was never reported broken *is* complete, whenever you ask.
     */
    private effectiveOutcome(): StreamOutcome {
        if (this.outcome === 'never_connected' && this.sentBytes > 0) {
            return 'completed'
        }
        return this.outcome
    }

    /**
     * Account for one worklet frame.
     *
     * A method rather than two lines inside the handler so it is reachable from a
     * test: jsdom has no AudioWorklet, so a test that drove the handler would have
     * to reimplement this arithmetic and would then pass while the real code was
     * wrong.
     *
     * Counting the dropped frames is the point. The `readyState` guard on its own is
     * how a dead socket became indistinguishable from a quiet contributor — audio
     * kept being produced and kept being thrown away, and nothing recorded that it
     * had been.
     */
    private recordFrame(bytes: number, accepted: boolean): void {
        if (accepted) this.sentBytes += bytes
        else this.droppedBytes += bytes
    }

    async start(): Promise<void> {
        const { stream, locale, tenantSlug, recordingId, onUpdate, onError } =
            this.options

        const ws = new WebSocket(streamUrl(locale, tenantSlug, recordingId))
        ws.binaryType = 'arraybuffer'
        this.ws = ws

        ws.onmessage = (event) => {
            let message: TranscriptMessage
            try {
                message = JSON.parse(event.data as string)
            } catch {
                return
            }
            if (message.type === 'error') {
                // After `stop()` the socket is only finishing; nothing is left to fail.
                if (this.closed) return
                this.outcome = 'errored'
                onError?.(message.detail ?? 'Live transcription failed')
                return
            }
            const next = applyTranscriptMessage(this.state, message)
            if (next !== this.state) {
                this.state = next
                onUpdate(renderTranscript(next))
            }
        }
        ws.onerror = () => {
            this.outcome = 'errored'
            onError?.('Live transcription disconnected')
        }

        await new Promise<void>((resolve, reject) => {
            ws.onopen = () => resolve()
            // Not a recording failure — the caller carries on without a preview.
            ws.onclose = () =>
                reject(new Error('Live transcription unavailable'))
        })

        // **Rebind `onclose`.** Until this line it is the promise's `reject`, which
        // after the promise settles is a no-op — so a socket that opened and then
        // dropped mid-recording reported *nothing*: no callback, no state change, no
        // error, while every subsequent frame was silently discarded by the
        // `readyState` guard below. That is the failure this whole contract exists to
        // make visible, and it was the one that looked most like success.
        const handleMidStreamClose = () => {
            if (this.closed) return // our own teardown; `stop()` detaches first anyway
            this.outcome = 'closed_early'
            onError?.('Live transcription stopped early')
        }
        ws.onclose = handleMidStreamClose
        // A close landing in the microtask gap between `resolve()` and the line above
        // would hit the old no-op handler and be lost. Cheap to close that window.
        if (
            ws.readyState === WebSocket.CLOSING ||
            ws.readyState === WebSocket.CLOSED
        ) {
            handleMidStreamClose()
        }
        // `start()` is fire-and-forget, so a contributor can stop inside either
        // await below. Without these checks the graph is built after teardown and
        // nothing references it — a live AudioContext and an open mic, against a
        // browser limit of ~6 contexts per page.
        if (this.closed) return

        const context = new AudioContext()
        this.context = context
        await context.audioWorklet.addModule(workletUrl)
        if (this.closed) {
            await context.close()
            return
        }

        const source = context.createMediaStreamSource(stream)
        const worklet = new AudioWorkletNode(context, 'pcm-downsampler')
        this.source = source
        this.worklet = worklet

        worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
            const accepted = ws.readyState === WebSocket.OPEN
            if (accepted) ws.send(event.data)
            this.recordFrame(event.data.byteLength, accepted)
        }
        // Terminates at the worklet: connecting to `context.destination` would
        // play the microphone back through the speakers and feed back.
        source.connect(worklet)
    }

    /**
     * Stop capturing, tell the server we are done, and release the audio graph.
     *
     * `recordedSeconds` is the denominator for the server's coverage metric — the
     * one part of the calculation the server cannot observe. It is deliberately the
     * *only* number reported: the numerator is what actually arrived, measured
     * server-side, so a client that is wrong or stale can understate coverage but
     * never overstate it.
     */
    async stop(recordedSeconds?: number): Promise<void> {
        if (this.closed) return
        this.closed = true

        try {
            this.source?.disconnect()
            this.worklet?.disconnect()
            if (this.worklet) this.worklet.port.onmessage = null
            await this.context?.close()
        } catch {
            // Teardown is best-effort — the recording is what matters.
        }
        this.context = this.source = this.worklet = null

        const ws = this.ws
        this.ws = null
        if (!ws) return
        if (ws.readyState === WebSocket.OPEN) {
            // Keep listening until the server closes: the last utterance's final
            // arrives after `stop`, and closing at once dropped it — the preview
            // ended on a stale interim while the server had the right words.
            void closeGracefully(
                ws,
                JSON.stringify({ type: 'stop', recordedSeconds }),
                ws.onmessage
            )
            return
        }
        // Detach first: a close during CONNECTING fires onclose, which would
        // otherwise report a teardown as a transcription failure.
        ws.onmessage = ws.onerror = ws.onclose = null
        if (ws.readyState === WebSocket.CONNECTING) {
            // close() on a CONNECTING socket is a no-op in some browsers, so wait
            // for it to open and shut it then — otherwise it leaks.
            ws.onopen = () => ws.close()
        }
    }
}
